import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { db } from '../firebase';
import { collection, getDocs, query } from 'firebase/firestore';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { Search, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';

const GlossaryCard = ({ item }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Helper to clean "Definition" prefix from text
    const cleanText = (text) => {
        // Remove "Definition" followed by optional characters, case insensitive
        return text.replace(/^Definition\s*[:-]?\s*/i, '');
    };

    // Function to strip HTML tags for the summary
    const getSummary = (html) => {
        const tmp = document.createElement("DIV");
        // Clean "Definition" label from HTML if possible
        let cleanHtml = html.replace(/^(<p>)?\s*<strong>?Definition<\/strong>?\s*[:-]?\s*(<\/p>)?/i, '');
        tmp.innerHTML = cleanHtml;
        let text = tmp.textContent || tmp.innerText || "";

        // Extra clean on the text content in case HTML clean missed (e.g. "DefinitionAI")
        text = cleanText(text);

        return text.substring(0, 140) + (text.length > 140 ? "..." : "");
    };

    // Clean definition for full view
    const cleanDefinition = (html) => {
        // Remove "Definition" word if it starts the block, handling p, h1-h6 tags
        return html.replace(/^(<(p|h[1-6])>)\s*(<strong>)?Definition(<\/strong>)?\s*[:-]?\s*(<\/\2>)/i, '');
    };

    return (
        <div
            className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden flex flex-col h-full ${isExpanded
                ? 'border-[#A2CB52] shadow-lg ring-1 ring-[#A2CB52]'
                : 'border-gray-100 hover:border-[#A2CB52]/50 hover:shadow-md'
                }`}
        >
            <div
                className="p-8 cursor-pointer flex-grow flex flex-col"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex justify-between items-start mb-6">
                    <h3 className="text-2xl font-bold text-[#20222D] flex items-center pr-4 leading-tight">
                        <BookOpen size={24} className="text-[#A2CB52] mr-3 flex-shrink-0" />
                        <Link to={`/glossary/${item.slug || item.id}`} className="hover:underline hover:text-[#A2CB52] transition-colors">
                            {item.term}
                        </Link>
                    </h3>
                    <button className="text-gray-400 hover:text-[#A2CB52] transition-colors flex-shrink-0 mt-1">
                        {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                </div>

                <div className={`transition-all duration-500 ease-in-out flex-grow`}>
                    {isExpanded ? (
                        <div
                            className="text-gray-600 prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-[#20222D] prose-a:text-[#A2CB52] prose-p:mb-4 prose-p:leading-relaxed prose-li:marker:text-[#A2CB52] break-words text-left hyphens-none"
                            dangerouslySetInnerHTML={{ __html: cleanDefinition(item.definition) }}
                        />
                    ) : (
                        <p className="text-gray-500 text-base leading-relaxed text-left">
                            {getSummary(item.definition)}
                        </p>
                    )}
                </div>

                {!isExpanded && (
                    <div className="mt-6 flex items-center text-[#A2CB52] text-sm font-bold group pt-4 border-t border-gray-50 uppercase tracking-wide">
                        Read Definition
                        <ChevronDown size={16} className="ml-1 group-hover:translate-y-1 transition-transform" />
                    </div>
                )}
            </div>
        </div>
    );
};

const Glossary = () => {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeLetter, setActiveLetter] = useState('A');
    const [availableLetters, setAvailableLetters] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const q = query(collection(db, 'glossary'));
                const querySnapshot = await getDocs(q);
                let termsData = querySnapshot.docs.map(doc => doc.data());

                // Client-side filter for published
                termsData = termsData.filter(term => term.published !== false);

                // Sort alphabetically
                termsData.sort((a, b) => a.term.localeCompare(b.term));

                setTerms(termsData);

                // Extract unique starting letters
                const letters = [...new Set(termsData.map(t => t.term.charAt(0).toUpperCase()))].sort();
                setAvailableLetters(letters);

                // Default to first letter if A is not present, or keep A
                if (letters.length > 0 && !letters.includes('A')) {
                    setActiveLetter(letters[0]);
                }
            } catch (error) {
                console.error("Error fetching glossary: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTerms();
    }, []);

    // Filter logic
    const filteredTerms = terms.filter(t => {
        const matchesLetter = activeLetter === 'ALL' || t.term.charAt(0).toUpperCase() === activeLetter;
        const matchesSearch = t.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.definition.toLowerCase().includes(searchTerm.toLowerCase());
        return searchTerm ? matchesSearch : matchesLetter;
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                title="BPO Glossary | Outsourcing Terms & Definitions"
                description="Comprehensive dictionary of Business Process Outsourcing (BPO) terms. Understand the industry jargon."
            />

            {/* Header Section */}
            <div className="bg-[#20222D] text-white pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="#A2CB52" />
                    </svg>
                </div>
                <div className="relative max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
                        BPO <span className="text-[#A2CB52]">Glossary</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10">
                        Decode the language of outsourcing. A curated collection of essential industry terms and definitions.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto relative">
                        <input
                            type="text"
                            placeholder="Search for a term..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 rounded-full text-gray-900 focus:ring-4 focus:ring-[#A2CB52]/50 border-none shadow-xl"
                        />
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <FadeIn>
                    {/* Letter Filter - Hide if searching */}
                    {!searchTerm && (
                        <div className="mb-12 overflow-x-auto pb-4">
                            <div className="flex flex-nowrap md:flex-wrap justify-center gap-2 min-w-max md:min-w-0">
                                <button
                                    onClick={() => setActiveLetter('ALL')}
                                    className={`px-4 py-2 rounded-lg font-bold transition-all text-sm ${activeLetter === 'ALL'
                                        ? 'bg-[#A2CB52] text-[#20222D] shadow-md'
                                        : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-[#20222D]'
                                        }`}
                                >
                                    ALL
                                </button>
                                {availableLetters.map((letter) => (
                                    <button
                                        key={letter}
                                        onClick={() => setActiveLetter(letter)}
                                        className={`w-10 h-10 rounded-lg font-bold transition-all text-sm flex items-center justify-center ${activeLetter === letter
                                            ? 'bg-[#A2CB52] text-[#20222D] shadow-md scale-110'
                                            : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-[#20222D]'
                                            }`}
                                    >
                                        {letter}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52] mx-auto"></div>
                        </div>
                    ) : filteredTerms.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
                            <div className="text-6xl mb-4">🔍</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No terms found</h3>
                            <p className="text-gray-500">Try adjusting your search or selecting a different letter.</p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="mt-4 text-[#A2CB52] font-bold hover:underline"
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-6">
                            {filteredTerms.map((item, index) => (
                                <GlossaryCard key={item.id || index} item={item} />
                            ))}
                        </div>
                    )}

                    {!searchTerm && filteredTerms.length > 0 && (
                        <div className="mt-12 text-center text-gray-400 text-sm">
                            Showing terms starting with <span className="font-bold text-[#20222D]">{activeLetter}</span>
                        </div>
                    )}
                </FadeIn>
            </div>
        </div>
    );
};

export default Glossary;
